
import {onErrorResumeNext as observableOnErrorResumeNext,  Observable ,  BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from '../api-service/api.service';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from '../storage-service/storage.service';
import { LoggerService } from '../logger-service/logger.service';


declare const System: any;

@Injectable()
export class ApplicationService {
    // Application version
    public version: string = '0.0.0001';
    public dict;
    // List of supported languages ('localname' contains its autoglottonym, ie. the word a language uses to refer to itself)
    public supportedLanguages: Array<{ code: string; locale: string; localname: string }> = [
        { code: 'en-US', locale: 'en', localname: 'English' },
        //{ code: 'en-GB', locale: 'en-GB', localname: 'English (UK)' },
        { code: 'de-DE', locale: 'de', localname: 'Deutsch' },
        { code: 'sp-ES', locale: 'es', localname: 'Español' },
        { code: 'fr-FR', locale: 'fr', localname: 'Français' },
        { code: 'it-IT', locale: 'it', localname: 'Italiano' },
        //{ code: 'nl-NL', locale: 'nl', localname: 'Nederlands' },
        { code: 'pt-PT', locale: 'pt', localname: 'Português' }
        //{ code: 'pt-BR', locale: 'pt-BR', localname: 'Português (Brasil)' },
        //{ code: 'zh-CN', locale: 'zh', localname: '簡體中文' }
    ];
    // Application's default language
    public defaultLanguage: string = 'en-US';

    // Name of OXE server
    public oxeName: string = document.location.hostname;

    private _configDetailWidthValue: BehaviorSubject<number>;

    constructor(private _apiService: ApiService, private _translate: TranslateService, private _storage: StorageService, private _logger: LoggerService) {
        this._configDetailWidthValue = new BehaviorSubject(600);
    }

    public loadVersion() {
        this._apiService.retrieveManifest()
            .subscribe(obj => {
                this.saveVersion(obj);
                return this.version;
            });
    }

    private saveVersion(obj: any) {
        if (!obj.version) {
            this._logger.warn('ApplicationService', 'Missing version in the application package.json file !');
            return;
        }
        this._logger.info('ApplicationService', 'Version: ' + obj.version);
        this.version = obj.version;
    }

    public retrieveAndMergeDictionnaries(lang: string): void {
        observableOnErrorResumeNext(this._apiService.retrieveDictionnary(lang), this._apiService.retrieveErrors(lang))
            .subscribe((translations) => {
                if(translations["Annu_Name"]!= undefined)
                this.dict = translations;
                // Merge oxe model and error translations into the application's translations
                this._translate.setTranslation(lang, translations, true);
            });
    }

    public setupApplicationLanguage(): void {
        // this language will be used as a fallback when a translation isn't found in the current language
        this._translate.setDefaultLang(this.defaultLanguage);
        this._translate.use(this.defaultLanguage); // important! use the fallback language before setting it to the initial language

        // the lang to use
        this._translate.use(this.getLanguage());
    }

    public getLanguage(): string {
        return this._storage.retrieveLanguage() || this.getSupportedLanguage();
    }

    public getSupportedLanguage(): string {
        // use navigator lang if available
        return this.getLanguageCode(navigator.language);
    }

    /*
     * Return the locale that best matches the specifed language code from the list of supported languages.
     * @param {String} language the language code to find best match (for example, "zh-CN", "fr-ca, "fr")
     * @return {String} the best matching code from the supported languages (for example, "zh-CN", "fr-FR, )
     */
    private getLanguageCode(language: string): string {
        // first, try to find exact matching
        const code = language.toLowerCase();
        let foundLanguage = this.supportedLanguages.find(lang => {
            return lang.code.toLowerCase() === code || lang.locale.toLowerCase() === code;
        });

        // otherwise, try to find country matching
        const country = code.substring(0, 2);
        foundLanguage = foundLanguage || this.supportedLanguages.find(lang => {
            return lang.locale.toLowerCase() === country;
        });

        if (foundLanguage) {
            return foundLanguage.code;
        }

        return this.defaultLanguage;
    }

    get configWidthValue(): number {
        return this._configDetailWidthValue.getValue();
    }

    set configWidthValue(value: number) {
        this._configDetailWidthValue.next(value);
    }

    public configWidthValueChanges(): Observable<number> {
        return this._configDetailWidthValue;
    }

}
